import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "@/libs/utils";
import Image from "next/image";
import ChevronLeftIcon from "@/v2-components/core/icon/chevron-left-icon";
import ChevronRightIcon from "@/v2-components/core/icon/chevron-right-icon";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  type CarouselApi,
} from "@/v2-components/ui/carousel";

/**
 * 구매 섹션 이미지 영역.
 *
 * - **태블릿 이상**: 세로 썸네일 열 + 라이트박스 1장 (피그마 28707:131807 `Left`).
 *   좌우 화살표 · 하단 바 인디케이터.
 *   1440 기준 썸네일 98 + 갭 24 + 라이트박스 814 = 936.
 *   (피그마는 라이트박스 830/좌측열 952 인데, 레일에 에러 배경용 좌우 패딩 16 이
 *    붙으면서 그만큼 좁아졌다 — 레일 콘텐츠 464 를 지키는 쪽을 택했다.
 *    라이트박스는 정사각 비율 유지라 16px 차이는 이미지에만 반영된다.)
 * - **모바일**: 스와이프 캐러셀 + 하단 바 인디케이터 (피그마 28707:130781 `Preview`).
 *   썸네일 스트립이 없어졌으므로 **스와이프가 유일한 이동 수단**이라 embla 를 쓴다.
 *
 * 이전 데스크톱 안은 2열 타일 그리드였다(로지텍 PDP 스타일, 페이지 스크롤로 훑음).
 * 새 안은 한 장씩 보는 캐러셀이라 세로 길이가 이미지 수와 무관해졌다.
 *
 * ⚠️ 두 블록이 같은 `images[0]` 을 각각 `priority` 로 렌더한다(CSS 로만 숨겨서 둘 다 DOM 에
 * 있음) → 프리로드 링크가 2종 생긴다. 실측상 모바일에서는 두 sizes 가 같은 후보(640w)로
 * 수렴해 요청이 합쳐지고, 데스크톱에서는 모바일용 작은 후보 하나가 추가로 붙는다.
 * 하나로 줄이려면 CSS 숨김 대신 `useIsMobileView` 조건 렌더가 필요한데 SSR/hydration
 * 트레이드오프가 생겨 지금은 수용한다.
 */
export function ProductImageGallery({
  images,
  productName,
  className,
}: {
  images: string[];
  productName: string;
  className?: string;
}) {
  const [active, setActive] = useState(0);

  // 색상/번들 전환으로 이미지 목록이 통째로 바뀌면 첫 장으로 되돌린다.
  // (안 하면 이전 선택의 인덱스가 남아 엉뚱한 장이 대표로 뜬다)
  //
  // 배열 **내용**을 키로 쓴다. `images` 참조로 걸면 ProductUtil.getProductImages 의
  // 폴백 경로가 매 렌더 새 배열(`[thumbnail]`)을 반환해서, 부모가 리렌더될 때마다
  // 선택이 조용히 1장으로 튕긴다.
  //
  // 모바일 스와이프 캐러셀 제어. **`active` 를 embla 로 밀어넣는 effect 는 두지
  // 않는다** — 이동은 전부 이벤트(썸네일 클릭·화살표·스와이프)에서 일어나므로 그
  // 자리에서 맞춘다. (effect + setState 를 늘리면 cascading render 경고가 쌓인다)
  //
  // 반대 방향(embla → `active`)은 구독이라 effect 가 맞다. api 를 **state 로** 받는
  // 이유가 이것 — ref 로 받으면 콜백 안에서 `on` 만 걸 수 있고 `off` 할 자리가 없다.
  // (지금 embla 는 reInit 때 api 인스턴스를 갈아끼우지 않아 리스너가 안 쌓이지만,
  //  그건 라이브러리 내부 구현에 기댄 가정이라 업그레이드에 깨질 수 있다)
  //
  // `src/hooks/useCarousel.ts` 의 `useCarouselState` 와 비슷하지만 그걸 쓸 수 없다 —
  // 그 훅은 인덱스를 **자기가 소유**해서 embla 이벤트로만 갱신한다. 여기선 데스크탑
  // 썸네일·화살표가 인덱스를 직접 정하는데, 그 시점 모바일 캐러셀은 `tablet:hidden`
  // (display:none, 크기 0)이라 `scrollTo` 가 `select` 를 안 흘릴 수 있다. 그래서
  // `active` 는 이쪽이 소유하고 embla 는 구독만 한다.
  const [embla, setEmbla] = useState<CarouselApi>();
  useEffect(() => {
    if (!embla) return;
    const sync = () => setActive(embla.selectedScrollSnap());
    embla.on("select", sync);
    // `resize` 도 듣는다 — 뷰포트가 바뀌면 embla 가 reInit 하며 다른 스냅에 안착할 수
    // 있는데, 그때 `active` 만 stale 이면 인디케이터·썸네일이 다른 장을 가리킨다.
    embla.on("resize", sync);
    return () => {
      embla.off("select", sync);
      embla.off("resize", sync);
    };
  }, [embla]);

  // ⚠️ `imagesKey` 당 **한 번만** 돈다. deps 에 `embla` 가 있어서(api 가 붙을 때
  // `scrollTo` 를 해줘야 한다) 가드가 없으면 두 번 돈다 — 썸네일 버튼은 SSR 되어
  // hydration 직후 클릭 가능한데 `embla` 는 그보다 늦게 도착하므로, 그 사이 고른
  // 이미지가 api 가 붙는 순간 **1번으로 되돌아간다.**
  const imagesKey = images.join("|");
  const resetDoneFor = useRef<string | null>(null);
  useEffect(() => {
    if (resetDoneFor.current === imagesKey) return;
    resetDoneFor.current = imagesKey;
    setActive(0);
    // 두 번째 인자 `true` = 애니메이션 없이 즉시 이동
    embla?.scrollTo(0, true);
  }, [imagesKey, embla]);

  const count = images.length;
  const carouselOpts = useMemo(
    () => ({ loop: true, watchDrag: images.length > 1 }),
    [images.length],
  );
  /** 데스크탑 썸네일·화살표에서 호출. 모바일 캐러셀 위치도 함께 맞춘다. */
  const select = useCallback(
    (index: number) => {
      setActive(index);
      embla?.scrollTo(index);
    },
    [embla],
  );

  // 이미지 목록이 줄어든 직후 한 프레임은 `active` 가 범위를 벗어난다(초기화 effect 는
  // 렌더 뒤에 돈다). **한 곳에서 clamp 해서 전부 이 값을 쓴다** — 예전엔 대표 이미지만
  // clamp 하고 썸네일 하이라이트·인디케이터는 raw `active` 를 봐서, 그 프레임에
  // 아무것도 선택되지 않은 것처럼 깜빡였다.
  const activeIndex = Math.min(active, Math.max(count - 1, 0));
  // 순환(wrap) — 피그마는 첫 장에서도 좌측 화살표가 활성 상태로 그려져 있다(28756:48434).
  const step = useCallback(
    (delta: number) => select((activeIndex + delta + count) % count),
    [activeIndex, count, select],
  );

  if (count === 0) return null;

  const main = images[activeIndex];
  const multiple = count > 1;

  return (
    <div className={className}>
      {/* 모바일 — 스와이프 캐러셀 + 하단 바 인디케이터 (피그마 28707:130781) */}
      {/* 썸네일 스트립이 사라져 **스와이프가 유일한 이동 수단**이다. 인디케이터는
          뷰어 밖 아래 16px(데스크탑은 뷰어 안에 겹쳐 있는 것과 다르다). */}
      <div className="flex flex-col gap-4 tablet:hidden">
        {/* ⚠️ `opts` 는 메모이즈해야 한다 — 인라인 객체면 매 렌더 새 참조라
            embla 가 계속 reInit 되고 드래그 중이던 제스처가 끊긴다. */}
        <Carousel setApi={setEmbla} opts={carouselOpts} className="w-full">
          <CarouselContent className="ml-0">
            {images.map((src, index) => (
              <CarouselItem key={`${src}-${index}`} className="pl-0">
                <div className="relative aspect-square w-full overflow-hidden rounded-2xl bg-coolgray-10">
                  <Image
                    src={src}
                    alt={`${productName} ${index + 1}`}
                    fill
                    // 768px 미만에서만 보인다. 페이지 좌우 패딩 16씩 제외.
                    sizes="(max-width: 767px) calc(100vw - 32px), 343px"
                    // 뷰어 내부 패딩 16 (28707:130782 — 328 안에 이미지 296)
                    className="object-contain p-4"
                    priority={index === 0}
                  />
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>
        {multiple && <Indicators count={count} active={activeIndex} />}
      </div>

      {/* 태블릿 이상 — 썸네일 열 + 라이트박스 (피그마 28707:131807) */}
      {/* ⚠️ `items-stretch` 를 쓰면 안 된다 — 썸네일 열이 행 높이를 결정해버려서
          라이트박스의 `aspect-square` 가 깨진다(실측 768px: 614×668).
          "썸네일 열 높이를 라이트박스에 묶기" 는 flex 로는 순환이라 불가능하다
          (행 높이 = 가장 큰 자식 = 썸네일 열).
          그래서 `items-start` 로 두고 썸네일 열은 콘텐츠 높이를 그대로 쓴다.
          이미지 8장 이상이면(8×98 + 7×16 = 896 > 830) 열이 라이트박스보다 길어지는데,
          실데이터는 최대 6장(668)이고 넘쳐도 깨지지 않고 아래로 늘어날 뿐이다. */}
      <div className="hidden gap-6 tablet:flex tablet:items-start">
        {multiple && (
          <div className="flex shrink-0 flex-col gap-4">
            {images.map((src, index) => (
              <button
                key={`${src}-${index}`}
                type="button"
                onClick={() => select(index)}
                aria-label={`${productName} ${index + 1}`}
                aria-current={index === activeIndex}
                className={cn(
                  "relative size-[98px] shrink-0 overflow-hidden rounded-lg bg-white transition-colors",
                  // 선택은 2px 파란 테두리(28707:131771), 미선택은 1px coolgray-20.
                  // 두께가 달라도 box-border 라 98px 은 유지된다(레이아웃 안 밀림).
                  index === activeIndex
                    ? "border-2 border-blue-60"
                    : "border border-coolgray-20 hover:border-coolgray-90",
                )}
              >
                <Image
                  src={src}
                  alt=""
                  fill
                  sizes="98px"
                  className="object-contain"
                />
              </button>
            ))}
          </div>
        )}
        <div className="relative aspect-square min-w-0 flex-1 overflow-hidden rounded-2xl bg-coolgray-10">
          <Image
            src={main}
            alt={`${productName} ${activeIndex + 1}`}
            fill
            // 실측 기반(페이지 좌우 패딩 32, 컨테이너 max 1440,
            // 레일 496 + 갭 8, 썸네일열 98 + 갭 24):
            //   <768   이 블록은 안 보이지만 **모바일 캐러셀과 같은 값**을 쓴다 ↓
            //   <1024  레일이 아래로 내려가 좌측열이 전폭 → 100vw - 32 - 122
            //   <1472  컨테이너 = 100vw - 32          → 100vw - 32 - 504 - 122
            //   이상   컨테이너 고정 1440             → 814px
            //
            // ⚠️ 첫 분기가 없으면 모바일에서 **안 보이는 이미지를 따로 받는다**.
            // 이 블록은 `hidden` 이지만 `priority`(=eager)라 src 가 바뀌면 그대로
            // 요청이 나가고, sizes 가 달라 캐러셀과 다른 후보를 고른다.
            // 실측: 스와이프 1회에 보이는 384w 외에 256w 가 추가로 다운로드됐다.
            sizes="(max-width: 767px) calc(100vw - 32px), (max-width: 1023px) calc(100vw - 154px), (max-width: 1471px) calc(100vw - 658px), 814px"
            className="object-contain p-10"
            priority
          />
          {multiple && (
            <>
              <SliderButton
                label={`${productName} previous image`}
                onClick={() => step(-1)}
                className="left-10"
              >
                <ChevronLeftIcon className="size-6" />
              </SliderButton>
              <SliderButton
                label={`${productName} next image`}
                onClick={() => step(1)}
                className="right-10"
              >
                <ChevronRightIcon className="size-6" />
              </SliderButton>
              {/* 데스크탑은 인디케이터가 뷰어 **안**에 겹친다 (하단 38px, 28756:48416) */}
              <Indicators
                count={count}
                active={activeIndex}
                className="absolute inset-x-0 bottom-[38px]"
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * 바 인디케이터 — 20×2, 갭 8, 활성 coolgray-50 / 비활성 coolgray-20
 * (데스크탑 28756:48416 · 모바일 28756:48423 — 값이 같아 공용).
 */
const Indicators = ({
  count,
  active,
  className,
}: {
  count: number;
  active: number;
  className?: string;
}) => (
  <div
    className={cn("flex items-center justify-center gap-2", className)}
    aria-hidden
  >
    {Array.from({ length: count }, (_, index) => (
      <span
        key={index}
        className={cn(
          "h-0.5 w-5 rounded-[2px] transition-colors",
          index === active ? "bg-coolgray-50" : "bg-coolgray-20",
        )}
      />
    ))}
  </div>
);

/** 라이트박스 좌우 원형 화살표 48×48 (피그마 28756:48430 `Slider Buttons`) */
const SliderButton = ({
  label,
  onClick,
  className,
  children,
}: React.PropsWithChildren<{
  label: string;
  onClick: () => void;
  className?: string;
}>) => (
  <button
    type="button"
    aria-label={label}
    onClick={onClick}
    className={cn(
      "absolute top-1/2 flex size-12 -translate-y-1/2 items-center justify-center",
      "rounded-full bg-coolgray-hover-10 text-coolgray-70 transition-colors",
      "hover:bg-coolgray-hover-20",
      className,
    )}
  >
    {children}
  </button>
);
